import { useState, useRef, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { Profile } from "@shared/schema";

interface MentionTextareaProps {
  value: string;
  onChange: (text: string) => void;
  onMentionedIdsChange: (ids: string[]) => void;
  profiles: Profile[];
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
  "data-testid"?: string;
}

function getInitials(p: Profile) {
  return `${p.firstName?.[0] || ""}${p.lastName?.[0] || ""}`.toUpperCase();
}

export function MentionTextarea({
  value,
  onChange,
  onMentionedIdsChange,
  profiles,
  placeholder,
  rows = 2,
  className,
  disabled,
  ...rest
}: MentionTextareaProps) {
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuIndex, setMenuIndex] = useState(0);
  const [mentionedIds, setMentionedIds] = useState<Set<string>>(new Set());
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const activeProfiles = profiles.filter((p) => p.isActive !== false);

  const filteredProfiles = mentionQuery !== null
    ? activeProfiles
        .filter((p) =>
          `${p.firstName} ${p.lastName}`.toLowerCase().includes(mentionQuery.toLowerCase())
        )
        .slice(0, 6)
    : [];

  const syncIds = (text: string, ids: Set<string>): Set<string> => {
    const next = new Set<string>();
    for (const id of ids) {
      const p = activeProfiles.find((x) => x.id === id);
      if (p && text.includes(`@${p.firstName} ${p.lastName}`)) {
        next.add(id);
      }
    }
    return next;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    const cursor = e.target.selectionStart ?? text.length;
    const textBeforeCursor = text.slice(0, cursor);
    const match = textBeforeCursor.match(/@([\w\u00C0-\u024F]*)$/);

    if (match) {
      setMentionQuery(match[1]);
      setMenuOpen(true);
      setMenuIndex(0);
    } else {
      setMentionQuery(null);
      setMenuOpen(false);
    }

    const newIds = syncIds(text, mentionedIds);
    if (newIds.size !== mentionedIds.size) {
      setMentionedIds(newIds);
      onMentionedIdsChange([...newIds]);
    }
    onChange(text);
  };

  const selectProfile = (p: Profile) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursor = textarea.selectionStart ?? value.length;
    const textBefore = value.slice(0, cursor);
    const match = textBefore.match(/@([\w\u00C0-\u024F]*)$/);
    const insertStr = `@${p.firstName} ${p.lastName} `;
    let newText: string;
    if (match) {
      const mentionStart = cursor - match[0].length;
      newText = value.slice(0, mentionStart) + insertStr + value.slice(cursor);
    } else {
      newText = value + insertStr;
    }
    const newIds = new Set(mentionedIds);
    newIds.add(p.id);
    setMentionedIds(newIds);
    onMentionedIdsChange([...newIds]);
    setMentionQuery(null);
    setMenuOpen(false);
    onChange(newText);
    setTimeout(() => {
      textarea.focus();
      const pos = newText.indexOf(insertStr) + insertStr.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!menuOpen || filteredProfiles.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMenuIndex((i) => Math.min(i + 1, filteredProfiles.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMenuIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredProfiles[menuIndex]) selectProfile(filteredProfiles[menuIndex]);
    } else if (e.key === "Escape" || e.key === " ") {
      setMenuOpen(false);
      setMentionQuery(null);
    }
  };

  useEffect(() => {
    if (!value) {
      setMentionedIds(new Set());
      setMenuOpen(false);
      setMentionQuery(null);
    }
  }, [value]);

  return (
    <div className={cn("relative", className)}>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className="w-full"
        {...rest}
      />
      {menuOpen && filteredProfiles.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1 w-64 bg-card border rounded-lg shadow-xl z-50 overflow-hidden">
          {filteredProfiles.map((p, i) => (
            <button
              key={p.id}
              type="button"
              className={cn(
                "w-full text-left px-3 py-2 flex items-center gap-2 text-sm transition-colors",
                i === menuIndex ? "bg-muted" : "hover:bg-muted/70"
              )}
              onMouseDown={(e) => { e.preventDefault(); selectProfile(p); }}
              onMouseEnter={() => setMenuIndex(i)}
              data-testid={`mention-option-${p.id}`}
            >
              <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center shrink-0 overflow-hidden">
                {p.avatarUrl
                  ? <img src={p.avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
                  : getInitials(p)
                }
              </span>
              <span className="truncate">{p.firstName} {p.lastName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function renderWithMentions(contenu: string, profiles: Profile[]): (string | React.ReactElement)[] {
  let parts: (string | React.ReactElement)[] = [contenu];
  for (const p of profiles) {
    const mention = `@${p.firstName} ${p.lastName}`;
    parts = parts.flatMap((part) => {
      if (typeof part !== "string") return [part];
      const segments = part.split(mention);
      return segments.flatMap((seg, idx) =>
        idx < segments.length - 1
          ? [seg, <span key={`${p.id}-${idx}`} className="inline-flex items-center bg-primary/10 text-primary rounded px-1 text-xs font-medium mx-0.5">{mention}</span>]
          : [seg]
      );
    });
  }
  return parts;
}
